// Display AI-generated step-by-step resolution instructions
import { Badge } from '@/components/ui/badge'
import { Loader2, AlertTriangle, Navigation } from 'lucide-react'
import type { GeneratedInstructions } from '@/types/issue'

interface GeneratedInstructionsCardProps {
  instructions: GeneratedInstructions | null
  isGenerating: boolean
}

export function GeneratedInstructionsCard({
  instructions, isGenerating,
}: GeneratedInstructionsCardProps): JSX.Element | null {
  if (isGenerating) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-kaycoo-teal-light bg-kaycoo-white p-3">
        <Loader2 className="h-4 w-4 animate-spin text-kaycoo-teal-dark" />
        <span className="text-sm text-kaycoo-charcoal-2">Generating resolution instructions...</span>
      </div>
    )
  }

  if (!instructions) return null

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-kaycoo-teal-light bg-kaycoo-white p-4">
      <div className="flex items-center gap-2">
        <Badge className="bg-kaycoo-teal-dark text-white text-xs">AI Instructions</Badge>
      </div>

      <p className="text-sm text-kaycoo-charcoal leading-relaxed">{instructions.summary}</p>

      {instructions.menuPath && (
        <div className="flex items-center gap-1.5 text-xs text-kaycoo-teal-dark">
          <Navigation className="h-3 w-3" />
          <span className="font-medium">Menu Path:</span> {instructions.menuPath}
        </div>
      )}

      {instructions.steps.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-kaycoo-charcoal-2">Steps</p>
          {instructions.steps.map((step) => (
            <div key={step.order} className="flex gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-kaycoo-teal-dark text-[10px] font-bold text-white">
                {step.order}
              </span>
              <div className="flex-1">
                <p className="text-sm text-kaycoo-charcoal">{step.instruction}</p>
                {step.expectedResult && (
                  <p className="mt-0.5 text-xs italic text-kaycoo-charcoal-2">
                    Expected: {step.expectedResult}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {instructions.warnings && instructions.warnings.length > 0 && (
        <div className="space-y-1">
          {instructions.warnings.map((warning, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded bg-amber-50 p-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {warning}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
